import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import type { ExoticComponent, ReactElement, ReactSVGElement } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  children: ReactElement,
  /**
   * E.g. `<circle cx="32" cy="32" r="32" />`,
   * `<><circle key="circle" cx="32" cy="32" r="32" />, <rect key="rect" height="32" width="100%" rx="5" y="74" /></>`,
   * etc.
   */
  customSkeleton?: ReactSVGElement | ExoticComponent<{ children: ReactSVGElement | ReactSVGElement[] }>,
  /**
   * E.g. '100%', '300px', etc.
   */
  height?: string,
  isLoading: boolean,
};

export const SkeletonLoader = ({ children, customSkeleton, height, isLoading }: Props) => {
  const { t } = useTranslation('common');
  const animationId = `shimmer-${LoadingBox.toString().replace('.', '')}`;
  const svgId = `shimmer-${Math.random().toString().split('.').pop()}`;

  return (
    <>
      {isLoading && (
        <>
          <Global styles={shimmer(animationId)} />

          <LoadingBox animationId={animationId} data-test="skeleton-loader" height={height} svgId={svgId}>
            <LoadingText>{t('loading')}</LoadingText>

            <Svg data-test="skeleton-loader-svg">
              <defs>
                <clipPath id={svgId}>
                  {customSkeleton || (
                    <>
                      <rect width="33%" height="32" rx="5" />
                      <rect height="calc(100% - 64px)" width="100%" rx="5" y="64" />
                    </>
                  )}
                </clipPath>
              </defs>
            </Svg>
          </LoadingBox>
        </>
      )}

      {/* Children need to be able to preload their content, so we need to render them to run these preload functions,
      but hide them until all preloading is finished. */}
      <ContentWrapper data-test="skeleton-loader-content-wrapper" isLoading={isLoading}>
        {children}
      </ContentWrapper>
    </>
  );
};

const shimmer = animationId => css`
  @keyframes ${animationId} {
    to {
      transform: translateX(100%);
    }
  }
`;

// Use `display: contents` so that the children can still be affected by grid/flex properties on the parent of the
// SkeletonLoader.
const ContentWrapper = styled.div(
  ({ isLoading }) => `
    display: ${isLoading ? 'none' : 'contents'};
  `
);

const LoadingBox = styled('div', { shouldForwardProp: prop => !['animationId', 'height', 'svgId'].includes(prop) })(
  ({ animationId, height, svgId }) => `
    display: flex;
    flex-direction: column;
    row-gap: 32px;
    position: relative;
    width: 100%;
    ${height ? `height: ${height};` : ''}
    min-height: 249px;
    overflow: hidden;
    background: #dddbdd;
    clip-path: url(#${svgId});

    &::after {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      content: '';
      background-image: linear-gradient(
        to right,
        transparent,
        hsl(0 0% 100% / 20%) 20%,
        hsl(0 0% 100% / 50%) 60%,
        transparent
      );
      transform: translateX(-100%);
      animation: ${animationId} 2s linear infinite;
    }
  `
);

const LoadingText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: polygon(0 0, 0 0, 0 0, 0 0);
`;

const Svg = styled.svg`
  position: absolute;
  width: 100%;
  height: 100%;
`;
